import { useEffect, useState, useRef } from "react";
import candidateApproval from "@/assets/stats/candidate-approval.webp";
import trainingSuccess from "@/assets/stats/training-success.webp";
import productivity from "@/assets/stats/productivity.webp";
import nps from "@/assets/stats/nps.webp";

const topCards = [
  { image: candidateApproval, label: "Candidate Approval Rate" },
  { image: trainingSuccess, label: "Training Success Rate" },
  { image: productivity, label: "Productivity Rate" },
  { image: nps, label: "Training Net Promoter Score" },
];

const kpiBars = [
  { label: "Customer Satisfaction", value: 95 },
  { label: "KPI Adherence", value: 91 },
  { label: "Employee Productivity", value: 95 },
  { label: "Agent Attrition", value: 5, inverted: true },
];

function AnimatedBar({ label, value, inverted, delay }: { label: string; value: number; inverted?: boolean; delay: number }) {
  const [width, setWidth] = useState(0);
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            setWidth(inverted ? 100 - value : value);
            const duration = 1200;
            const steps = 30;
            const increment = value / steps;
            let current = 0;
            const timer = setInterval(() => {
              current += increment;
              if (current >= value) {
                setCount(value);
                clearInterval(timer);
              } else {
                setCount(Math.round(current));
              }
            }, duration / steps);
          }, delay);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value, inverted, delay]);

  return (
    <div ref={ref} className="bg-background rounded-2xl p-5 shadow-card animate-fade-up" style={{ animationDelay: `${delay}ms` }}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-semibold text-foreground">{label}</p>
        <span className="text-lg font-bold text-primary">{count}%</span>
      </div>
      <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-1000 ease-out bg-gradient-to-r from-secondary to-primary"
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
}

export function DeliveryData() {
  return (
    <section className="py-10 md:py-14 bg-pale-pink">
      <div className="container-main">
        {/* Header */}
        <div className="flex flex-col items-center mb-8">
          <span className="section-label mb-3">
            Statistics
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold uppercase text-primary-foreground bg-secondary px-4 py-2 break-words">
            Delivery, Backed by Data
          </h2>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto text-center">
            IntelliBPO delivers consistent results through measured performance across recruitment, training, QA and daily operations.
          </p>
        </div>

        {/* Top metric cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {topCards.map((card, i) => (
            <div key={i} className="bg-background rounded-2xl p-6 shadow-card text-center animate-fade-up" style={{ animationDelay: `${i * 80}ms` }}>
              <img src={card.image} alt={card.label} className="w-full h-auto mb-4" />
              <p className="text-sm font-medium text-foreground">{card.label}</p>
            </div>
          ))}
        </div>

        {/* KPI Bars */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {kpiBars.map((bar, i) => (
            <AnimatedBar
              key={i}
              label={bar.label}
              value={bar.value}
              inverted={bar.inverted}
              delay={i * 100}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
